import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styled, { createGlobalStyle } from 'styled-components';
import { VideoProjectContext } from '@contexts/VideoProject.context';
import VideoProjectView from '@routes/userRoutes/projects/videoProject/VideoProjectView';

/**
 * Development only (registered when import.meta.env.DEV): the video project page in every job state, on the local
 * samples in public/dev-overlay/<sample>/ (not committed), without a backend.
 * /dev/video-flow?state=results|analyse|waiting|stopped&sample=customer_line|snow_1m34|m3t_0010
 * (m3t_0010 also has telemetry.json: the flight map next to the video)
 */

const DevBar = styled.div`
	position: fixed;
	z-index: 50;
	right: 12px;
	bottom: 12px;
	display: flex;
	gap: 0.75rem;
	padding: 0.4rem 0.75rem;
	border-radius: 10px;
	background: rgba(13, 15, 13, 0.92);
	border: 1px solid #2e322e;
	color: #858a85;
	font-size: 0.75rem;
	a {
		color: #b4b9b4;
	}
`;

// the cookie banner covers the page in headless screenshots
const HideCookieBanner = createGlobalStyle`
	#CybotCookiebotDialog, #CybotCookiebotDialogBodyUnderlay { display: none !important; }
`;

const STATES = ['results', 'analyse', 'waiting', 'stopped'];
const PROJECT = 'dev-project';
const JOBS = ['dev-job-1', 'dev-job-2'];
const ISO = '2026-10-16T12:02:00Z';

const jobFor = (state, sample) => ({
	uuid: JOBS[1],
	job_status: { results: 'finished', analyse: 'running', waiting: 'pending', stopped: 'failed' }[
		state
	],
	progress: { results: 100, analyse: 44, waiting: 1, stopped: -1 }[state],
	message: {
		results: 'Video analysis completed',
		analyse: 'Finding and tracking animals: 612 of 1330 frames, 97 counted so far',
		waiting:
			'Waiting for a free GPU machine; the job starts automatically when one is available',
		stopped: 'Video detection failed: InsufficientInstanceCapacity',
	}[state],
	created_at: ISO,
	counting_method: sample === 'customer_line' ? 'line_based' : 'unique_id',
	counting_scenario: 'fence_exit',
	model_id: 'BFM',
	model_version: 'base_models/bfm/trained/reindeer-bfm-v8/reindeer-bfm-v8.pt',
	analysis_start_seconds: 0,
	analysis_end_seconds: { customer_line: 44.4, m3t_0010: 16.0 }[sample] ?? 3.3,
	error_message: state === 'stopped' ? 'InsufficientInstanceCapacity' : null,
	// waiting: started 17 min ago, in server-local time like the API's fields
	capacity_waiting_since:
		state === 'waiting'
			? new Date(Date.now() - 17 * 60000 - new Date().getTimezoneOffset() * 60000)
					.toISOString()
					.replace('Z', '')
			: null,
	capacity_max_wait_hours: state === 'waiting' ? 12 : null,
	live_progress:
		state === 'analyse' ? { frames_done: 612, frames_total: 1330, counted_so_far: 97 } : null,
});

const VideoFlowDevPage = () => {
	const q = new URLSearchParams(window.location.search);
	const state = STATES.includes(q.get('state')) ? q.get('state') : 'results';
	const sample = q.get('sample') || 'customer_line';
	const base = `/dev-overlay/${sample}`;
	const [stats, setStats] = useState(null);
	const queryClient = useQueryClient();

	// ?t=30 starts the video at 30 s
	useEffect(() => {
		const t = Number(q.get('t'));
		if (!t) return undefined;
		const id = setInterval(() => {
			const v = document.querySelector('video');
			if (v && v.readyState >= 1) {
				v.currentTime = t;
				clearInterval(id);
			}
		}, 200);
		return () => clearInterval(id);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		fetch(`${base}/statistics.json`)
			.then(r => r.json())
			.then(setStats)
			.catch(() => setStats({}));
	}, [base]);

	const job = useMemo(() => jobFor(state, sample), [state, sample]);
	// the runs list reads earlier jobs from the query cache; fill it so no request goes out
	useMemo(() => {
		queryClient.setQueryData(['videoResults', PROJECT, JOBS[0]], {
			success: true,
			data: {
				...jobFor('stopped', sample),
				uuid: JOBS[0],
				created_at: '2026-10-16T11:15:00Z',
			},
		});
		queryClient.setQueryData(['videoResults', PROJECT, JOBS[1]], { success: true, data: job });
	}, [queryClient, job, sample]);

	const value = useMemo(
		() => ({
			project: {
				uuid: PROJECT,
				title:
					{ customer_line: 'Barmark spring migration', m3t_0010: 'Herd on snow · M3T' }[
						sample
					] ?? 'Sno · flight 309',
				tags: ['reindeer', '2026'],
				jobs: JOBS,
			},
			projectUuid: PROJECT,
			isLoading: !stats,
			error: null,
			refetch: async () => {},
			videoUuid: 'dev-video',
			videoData: {
				video_filename:
					{
						customer_line: 'client_video_1.mp4',
						m3t_0010: 'DJI_20250124125851_0010_V.MP4',
					}[sample] ?? 'DJI_20251203114350_0002_V.mp4',
				file_size: 161300000,
				duration_seconds: job.analysis_end_seconds,
				resolution_width: 3840,
				resolution_height: 2160,
				created_at: '2026-10-08T09:41:00Z',
				s3_raw_path: `${base}/clean_video.mp4`,
			},
			jobUuid: JOBS[1],
			latestJobUuid: JOBS[1],
			setSelectedJobUuid: () => {},
			resultsData: job,
			analysisData:
				state === 'results' && stats
					? {
							statistics: stats,
							virtual_line: stats.virtual_line,
							analysis_start_seconds: 0,
							analysis_end_seconds: job.analysis_end_seconds,
							result_paths: {
								tracks: `${base}/tracks.json`,
								clean_video: `${base}/clean_video.mp4`,
								...(sample === 'm3t_0010' && {
									telemetry: `${base}/telemetry.json`,
								}),
							},
						}
					: null,
		}),
		[stats, job, state, sample, base]
	);

	return (
		<>
			<HideCookieBanner />
			<DevBar>
				<b style={{ color: '#e9ece9' }}>Video flow dev</b>
				{STATES.map(s => (
					<a key={s} href={`?state=${s}&sample=${sample}`}>
						{s}
					</a>
				))}
				<span>·</span>
				{['customer_line', 'snow_1m34', 'm3t_0010'].map(s => (
					<a key={s} href={`?state=${state}&sample=${s}`}>
						{s}
					</a>
				))}
			</DevBar>
			<VideoProjectContext.Provider value={value}>
				<VideoProjectView />
			</VideoProjectContext.Provider>
		</>
	);
};

export default VideoFlowDevPage;
